'use client';
import { useEffect, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
  details?: { label: string; value: string }[];
  confirmationNumber?: string;
}

const ConfirmationModal = ({
  isOpen,
  onClose,
  title,
  message,
  details,
  confirmationNumber,
}: ConfirmationModalProps) => {
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isOpen) {
      // Focus close button when modal opens
      closeButtonRef.current?.focus();
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Handle Escape key
      const handleEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') {
          onClose();
        }
      };
      document.addEventListener('keydown', handleEscape);
      
      return () => {
        document.body.style.overflow = 'unset';
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-scale-in">
        {/* Success Icon */}
        <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
          <Icon name="CheckCircleIcon" size={48} className="text-green-600" />
        </div>

        {/* Title */}
        <h2 id="modal-title" className="text-2xl font-bold text-center text-foreground mb-3">
          {title}
        </h2>

        {/* Message */}
        <p id="modal-description" className="text-center text-muted-foreground mb-6">
          {message}
        </p>

        {/* Confirmation Number */}
        {confirmationNumber && (
          <div className="bg-gray-50 rounded-xl p-4 mb-6 text-center">
            <p className="text-sm text-muted-foreground mb-1">Confirmation Number</p>
            <p className="text-2xl font-bold text-primary" aria-label={`Confirmation number ${confirmationNumber}`}>
              {confirmationNumber}
            </p>
          </div>
        )}

        {/* Details */}
        {details && details.length > 0 && (
          <div className="bg-gray-50 rounded-xl p-6 mb-6">
            <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2">
              <Icon name="DocumentTextIcon" size={20} className="text-primary" />
              Submission Summary
            </h3>
            <dl className="space-y-3">
              {details.map((detail, index) => (
                <div key={index} className="flex justify-between text-sm">
                  <dt className="text-muted-foreground font-medium">{detail.label}:</dt>
                  <dd className="text-foreground font-semibold text-right ml-4">{detail.value}</dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {/* Close Button */}
        <button
          ref={closeButtonRef}
          onClick={onClose}
          className="w-full bg-primary text-primary-foreground font-semibold rounded-full px-8 py-4 hover:bg-primary/90 transition-all focus:outline-none focus:ring-4 focus:ring-primary/30"
          aria-label="Close confirmation dialog"
        >
          Close
        </button>
      </div>
    </div>
  );
};

export default ConfirmationModal;